import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { trackOnboardingComplete } from '../../lib/metaPixel';

type OnboardingContext = {
  createdWorkspaceId?: string | null;
};

export function OnboardingStep4() {
  const navigate = useNavigate();
  const { user, workspace, profile, fetchProfile } = useAuthStore();
  const [statusText, setStatusText] = useState('Preparing your prompts...');
  const [titleDots, setTitleDots] = useState('.');
  const [onboardingContext] = useState<OnboardingContext | null>(() => {
    const rawContext = sessionStorage.getItem('lima_onboarding_context');
    if (!rawContext) return null;

    try {
      return JSON.parse(rawContext) as OnboardingContext;
    } catch {
      return null;
    }
  });
  const [progressPercent, setProgressPercent] = useState(0);
  const hasStarted = useRef(false);

  useEffect(() => {
    const frames = ['.', '..', '...', '....', '...', '..'];
    let index = 0;

    const interval = window.setInterval(() => {
      index = (index + 1) % frames.length;
      setTitleDots(frames[index]);
    }, 450);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const startedAt = Date.now();

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const percent = Math.min(95, (elapsed / 60000) * 100);
      setProgressPercent(percent);
    }, 250);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const targetWorkspaceId = onboardingContext?.createdWorkspaceId || workspace?.id;

    if (!user || !targetWorkspaceId || hasStarted.current) return;
    hasStarted.current = true;

    const setupDashboard = async () => {
      // Typed in directly? Send them back to the first step they haven't finished,
      // since everything below marks onboarding complete.
      const [competitorsResult, promptsResult] = await Promise.all([
        supabase.from('competitors').select('id', { count: 'exact', head: true }).eq('workspace_id', targetWorkspaceId),
        supabase.from('tracked_prompts').select('id', { count: 'exact', head: true }).eq('workspace_id', targetWorkspaceId),
      ]);
      const incompleteStep = !workspace?.brand_url
        ? '/onboarding/step-1'
        : !competitorsResult.count
          ? '/onboarding/step-2'
          : !promptsResult.count
            ? '/onboarding/step-3'
            : null;
      if (incompleteStep) {
        navigate(incompleteStep, { replace: true });
        return;
      }

      let resolvedWorkspace = workspace;

      try {
        if (!resolvedWorkspace || resolvedWorkspace.id !== targetWorkspaceId) {
          const { data, error } = await supabase
            .from('workspaces')
            .select('*')
            .eq('id', targetWorkspaceId)
            .single();

          if (error || !data) throw error || new Error('Workspace not found');
          resolvedWorkspace = data;
        }

        useAuthStore.getState().setWorkspace(resolvedWorkspace);
        setStatusText('Querying AI search engines...');

        const { error } = await supabase.functions.invoke('run-prompts', {
          body: { workspace_id: resolvedWorkspace.id },
        });

        if (error) {
          console.error('Error running prompts:', error);
          setStatusText('Finishing up...');
        } else {
          setStatusText('Finding additional competitors...');
          const { error: suggestionError } = await supabase.functions.invoke('find-suggested-competitors', {
            body: {
              workspace_id: resolvedWorkspace.id,
              brand_url: resolvedWorkspace.brand_url,
            },
          });
          if (suggestionError) {
            console.error('Error finding suggested competitors:', suggestionError);
          }
          setStatusText('Dashboard ready!');
        }
      } catch (error) {
        console.error('Error during setup:', error);
      } finally {
        setProgressPercent(100);
        sessionStorage.removeItem('lima_onboarding_context');
        await supabase
          .from('profiles')
          .update({ onboarding_completed: true, workspace_id: targetWorkspaceId })
          .eq('id', user.id);
        if (!useAuthStore.getState().viewingAs) trackOnboardingComplete(targetWorkspaceId).catch(console.error);

        const nextProfile = profile
          ? { ...profile, onboarding_completed: true, workspace_id: targetWorkspaceId }
          : null;
        const latestWorkspace =
          useAuthStore.getState().workspace?.id === targetWorkspaceId
            ? useAuthStore.getState().workspace
            : resolvedWorkspace;

        if (nextProfile) {
          useAuthStore.getState().setProfile(nextProfile);
        }
        useAuthStore.getState().setWorkspace(latestWorkspace);
        fetchProfile(user.id).catch(console.error);
        navigate('/overview', { replace: true });
      }
    };

    setupDashboard();
  }, [user, workspace, profile, onboardingContext?.createdWorkspaceId, fetchProfile, navigate]);

  return (
    <div className="page-wrapper">
      <div className="app-wrapper">
        <div id="no-saved-ads" className="onboarding-wrapper">
          <div className="intro-wrapper">
            <div className="onboarding-setup-progress-wrap">
              <div className="remaining-range onboarding-setup-progress">
                <div
                  className="range-filled onboarding-setup-progress-fill"
                  style={{ width: `${Math.max(0, Math.min(100, progressPercent))}%` }}
                />
              </div>
            </div>
            <div className="spacer-medium"></div>
            <div className="text-size-large text-weight-semibold text-colour-dark-brown onboarding-setup-title">
              <span>We're setting up your dashboard</span>
              <span className="onboarding-setup-title-dots" aria-hidden="true">{titleDots}</span>
            </div>
            <div className="spacer-xxtiny"></div>
            <div className="text-size-regular text-colour-alt-grey align-center max-width-small">
              {statusText}
            </div>
            <div className="spacer-small"></div>
            <div className="card is-setup">
              <div className="text-size-regular text-weight-semibold text-colour-dark-brown">
                What happens during setup?
              </div>
              <div className="text-size-regular text-colour-alt-grey align-center max-width-small">
                We're using your prompts across the top AI search engines to begin understanding how your competitors rank. You can add more competitors and prompts manually.
              </div>
            </div>
          </div>
        </div>
        <div className="gradient-style"></div>
      </div>
    </div>
  );
}
